import type { ResBloqueioView } from '../components/ResBloqueioAviso';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { runaMarcarOnHub, runaPortalOnHub, runaRecallOnHub, askTellCatchup, cancelarDesafioOnHub, cancelarMagiaOnHub, cancelarResgateOnHub, corpoPendenteOnHub, resgatarCorpoOnHub, responderRessOnHub, cancelTradeOnHub, connectCityHub, desafiarOnHub, desistirDueloOnHub, dueloCarregouOnHub, invitePartyOnHub, inviteTradeOnHub, isEmptyGuid, kickPartyOnHub, leavePartyOnHub, offerOnHub, openCorpseOnHub, readyTradeOnHub, respondPartyOnHub, responderDueloOnHub, respondTradeOnHub, sairDueloOnHub, sameGuid, sayOnHub, startCityHub, takeCorpseAllOnHub, takeCorpseOnHub, tellOnHub, voltarDueloOnHub, type AllianceInvite, type GanhoDoPerfil, type GuildInvite, type PartyView, type WarInvite } from '../realtime/cityHub';
import { avisarAcessoEncerrado } from '../state/acessoEncerrado';
import { anotarDiario, createWorld, flutuarPrata } from './engine';
import { carregarMapaPorCidade } from './mapa';
import { overlayDoPack, prefetchFolhas } from './spriteAtlas';
import { api } from '../api';
import type { ChefeEstado, EventoEstado, PentaAltarLinha, PentaCanal, PentaEstado } from '../api/evento';
import type { LugarPenta } from './pentagrama';
import type { ColetaAoVivo, Pack } from '../api/types';
import { prefetchIcons } from '../assets/items';
import { avancarFila, filaVazia, receberAviso, type AvisoItem } from '../avisos/fila';
import { pecasDoAviso } from '../avisos/pecas';
import { mostrarAvisosNaTela } from '../avisos/preferencia';
import { ehAvisoDeEvento } from '../avisos/regras';
import { playAviso, playBanco, playPegar, playPortal } from '../audio/mixer';
import { ehConsumivel } from '../data/consumiveis';
import { t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { definirBuffs, definirEfeitosDe, limparEfeitosDeOutros } from './buffs';

function consumiveisDoPack(pack: Pack): Record<string, number> {
  const next: Record<string, number> = {};
  for (const i of pack.items) {
    if (i.equippedSlot || !ehConsumivel(i.defId)) continue;
    next[i.defId] = (next[i.defId] ?? 0) + i.qty;
  }
  return next;
}

/** `TradeOverCode` do fio → chave. O `1` (Done) leva o nome do parceiro. */
const CHAVE_TRADE_OVER: Readonly<Record<number, string>> = {
  1: 'troca.done',
  2: 'troca.refused',
  3: 'troca.cancel',
  4: 'troca.tooFar',
  5: 'troca.gone',
  6: 'troca.weight',
  7: 'troca.fail'
};
import type { PackItem } from '../api/types';
import type { CargaMundo, CastPulse, RecargaPulse, ChatLine, CorpseContents, DueloConvite, DueloEnviado, DueloEstado, DueloVoltar, JournalLine, JournalTone, NetStrike, TellCatchup, TradeSide, TradeSlot, TradeState, TreinoAlvo, WorldHandle, WorldStatus } from './types';
import type { PortalNoMapa } from './portais';
import { pontoDaViagem, type ViagemDestino } from './viagens';
import type { NpcNoMapa } from './npcs';

/** A fila de ganhos do perfil (aviso 99, 167 e 255): o perfil aplica os de seq maior que o último que viu. */
export interface GanhosDoPerfil { readonly seq: number; readonly itens: readonly (GanhoDoPerfil & { readonly seq: number })[] }

interface UseWorldArgs {
  playerName: string;
  /** 0 jogador, 1 GM, 2 admin. */
  staffCargo?: number;
  /** Chave do catálogo de mapas — `britain`, `Moonglow`, `Skara Brae`… */
  homeCity?: string;
  characterId?: string;
  /** Pasta em /sprites/character/{skin}/. */
  skin?: string;
  skinHue?: number;
  dead?: boolean;
  notoriety?: number;
  onDead?: (dead: boolean) => void;
  net?: boolean;
  /** `info`: a notoriedade, a relação (3 grupo, 1 guilda, 2 inimigo) e as casas até ele, para o menu radial. */
  onPlayerMenu?: (name: string, clientX: number, clientY: number, guildTag?: string, cargo?: number, info?: { notoriety: number; rel: number; dist: number }) => void;
  onMapa?: (id: string) => void;
  onDummyMenu?: (id: number, clientX: number, clientY: number) => void;
  onPlayerHover?: (hit: { name: string; x: number; y: number } | null) => void;
  /** Clique num NPC da praça. */
  onNpcClick?: (id: string, nome: string, titulo: string, x: number, y: number, temLoja: boolean, temCorretor: boolean, temBanco: boolean, remoteId: number, alcance: number) => void;
  onBankOpen?: (npcId: string, nome: string, x: number, y: number, remoteId: number, alcance: number) => void;
  /** Tell de outra pessoa — o mundo abre a janela sozinho. */
  onIncomingTell?: (name: string) => void;
  /** O servidor reservou um manequim para o treino automático: anda até ele e treina. */
  onTreinoAlvo?: (alvo: TreinoAlvo) => void;
}

export type ResEstadoView = {
  etapa: number;
  pontoId: string;
  cidade: string;
  x: number;
  y: number;
};

/** A etapa que a tela de carregamento mostra (techspec-loading). */
export type CargaTela = Omit<CargaMundo, 'etapa'> & { readonly etapa: CargaMundo['etapa'] | 'conectar' | 'reconectar' };

/** O nome de uma cidade a partir do id (`minoc` → Minoc) até o servidor mandar o rótulo. */
export function nomeDaCidade(id: string): string {
  const s = (id || '').replace(/[-_]+/g, ' ').trim();
  return s ? s.replace(/(^|\s)(\p{L})/gu, (_m, a: string, c: string) => a + c.toUpperCase()) : 'Britannia';
}

interface UseWorldResult {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  minimapRef: React.RefObject<HTMLCanvasElement>;
  paperdollRef: React.RefObject<HTMLCanvasElement>;
  profileDollRef: React.RefObject<HTMLCanvasElement>;
  inventoryDollRef: React.RefObject<HTMLCanvasElement>;
  status: WorldStatus | null;
  vip: {
    ate: number; mostrar: boolean; prata: number; semBanco: boolean;
    precos: Record<string, number>;
  } | null;
  /** Coleta aberta na sala (evento `co`), ou null. */
  coleta: ColetaAoVivo | null;
  /** Quando o passo foi recusado por estar coletando (ms); 0 nunca. */
  coletaTravadoEm: number;
  /** O último ganho de skill ou atributo (com número de série), para o perfil aberto se acertar. */
  /** Os ganhos recentes do perfil, em fila (vários chegam no mesmo tique: a skill, a Táticas, a barra). */
  ganhoDoPerfil: GanhosDoPerfil | null;
  /** Mock: a coleta trava o passo local sem sala. */
  travarColeta(on: boolean): void;
  /** Boneco de outro jogador da sala (Inspecionar). Devolve quem desfaz. */
  bonecoDe(nome: string, canvas: HTMLCanvasElement): () => void;
  marcarJogador(nome: string): boolean;
  retratoDoAlvo(canvas: HTMLCanvasElement, id: number): () => void;
  idDoJogador(nome: string): number | null;
  retratoDe(canvas: HTMLCanvasElement, nome: string, look: import('../realtime/cityHub').RetratoDoMembro | null): () => void;
  /** O provador do guarda-roupa. Devolve quem desfaz. */
  provador(canvas: HTMLCanvasElement, cloth: string, dir: number, feminino?: boolean): () => void;
  journal: readonly JournalLine[];
  combat: readonly JournalLine[];
  /** Aba Local: falas de quem está na tela (vêm no frame, sem anel no servidor). */
  local: readonly ChatLine[];
  global: readonly ChatLine[];
  trade: readonly ChatLine[];
  guild: readonly ChatLine[];
  alliance: readonly ChatLine[];
  tells: Readonly<Record<string, readonly ChatLine[]>>;
  tellNotice: string | null;
  unreadTells: Readonly<Record<string, number>>;
  sala: { mapaId: string; mapaNome: string; npcs: readonly (NpcNoMapa & { remoteId: number })[]; portais: readonly PortalNoMapa[]; viagens: readonly ViagemDestino[] };
  ready: boolean;
  failure: string | null;
  /** A tela de carregamento (techspec-loading). `pronto`: some. `conectar` e `reconectar` saem daqui; o resto, do motor. */
  carga: CargaTela;
  say(text: string, tone?: JournalTone): void;
  sayKey(chave: string, args?: Record<string, string | number>, tone?: JournalTone): void;
  falarNpc(id: number, text: string): void;
  sayToServer(via: 0 | 1 | 2 | 4 | 5, text: string): void;
  tellToServer(name: string, text: string): void;
  openTell(name: string): void;
  markTellRead(name: string): void;
  net: boolean;
  deal: TradeState | null;
  dealInvite: string | null;
  duelo: DueloEstado | null;
  dueloResultado: DueloEstado | null;
  dueloConvite: DueloConvite | null;
  dueloVoltar: DueloVoltar | null;
  /** O desafio que você mandou e espera resposta (duelo v2). */
  dueloEnviado: DueloEnviado | null;
  desafiar(nome: string, modo: 'sem_loot' | 'com_loot', aposta: boolean): void;
  cancelarDesafio(): void;
  responderDuelo(aceitar: boolean): void;
  desistirDuelo(): void;
  /** Atalho: a magia em curso falha de propósito (sem gastar mana). */
  cancelarMagia(): void;
  sairDuelo(): void;
  esquecerDuelo(): void;
  limparDueloResultado(): void;
  voltarDuelo(voltar: boolean): void;
  /** `null` = sem grupo, e o gump some. Vem do canal do grupo, não do motor. */
  party: PartyView | null;
  partyInvite: string | null;
  invitePartyMember(name: string): void;
  respondParty(ok: boolean): void;
  leaveParty(): void;
  kickPartyMember(name: string): void;
  corpses: readonly CorpseContents[];
  closeCorpse(id?: number): void;
  takeCorpse(id: number, itemId: string, qty?: number): Promise<void>;
  takeCorpseAll(id: number, slotIds: readonly string[]): Promise<void>;
  guildInvite: GuildInvite | null;
  allianceInvite: AllianceInvite | null;
  warInvite: WarInvite | null;
  clearGuildInvite(): void;
  clearAllianceInvite(): void;
  clearWarInvite(): void;
  inviteTrade(name: string): void;
  respondTrade(ok: boolean): void;
  /** `qty`: quantas da pilha (padrão: a pilha inteira). */
  offerItem(item: PackItem, qty?: number): void;
  takeBack(itemId: string): void;
  offerGold(qty: number): void;
  readyTrade(ready: boolean): void;
  cancelTrade(): void;
  setMount(defId: string | null): void;
  setCloth(defId: string | null): void;
  setVitals(id: number, hits: number, hitsMax: number, mana: number, manaMax: number, poisoned?: boolean): void;
  getTargetId(): number | null;
  getTargetKind(): import('./types').TargetKind | null;
  getTargetInfo(): { id: number; nome: string; kind: import('./types').TargetKind; notoriety: number } | null;
  getSelfNotoriety(): number;
  spawnLocalMob(mob: { id: number; name: string; x: number; y: number; defId: string; hits: number; hitsMax: number; range: number }): void;
  removeLocalMob(id: number): void;
  pickTile(hint?: string): Promise<{ x: number; y: number } | null>;
  cancelTilePick(): void;
  teleportSelf(x: number, y: number): void;
  setOculto(ligado: boolean): void;
  trocarMapa(id: string): Promise<void>;
  applyFizzle(): void;
  cast(slot: number, targetId: number): void;
  attack(targetId: number): void;
  trainDummy(dummyId: number, skill: string): void;
  stopTrainDummy(): void;
  approachDummy(dummyId: number, alcance: number, onArrive: () => void, alvo?: { x: number; y: number }): boolean;
  irPara(x: number, y: number): boolean;
  manequimDeTreinoPerto(): number | null;
  dummyEm(x: number, y: number): number | null;
  /** Menu Treinar (docs/techspec-treino-automatico.md): o servidor escolhe o manequim e responde com `onTreinoAlvo`. */
  treinarAuto(skill: string): void;
  playerScreen(): { x: number; y: number } | null;
  alvoAFrente(): { x: number; y: number; z: number } | null;
  listenStrike(fn: ((s: NetStrike, meu: boolean, emVoce: boolean, nome: string) => void) | null): void;
  listenStrikeCap(fn: (() => void) | null): void;
  youId: number;
  dummyTrain: { skill: string; dummyId: number; skills: readonly { id: string; value: number; startValue: number }[] } | null;
  castRefuse: number;
  consumeRefuse: number;
  /** A IA da caçada usou um consumível: a barra mostra a recarga do slot igual. */
  consumePulse: { seq: number; id: string; ms: number } | null;
  castPulse: CastPulse | null;
  /** A recarga de uma magia (techspec-combate-v2). */
  recargaPulse: RecargaPulse | null;
  /** Uma casa do mapa mudou (techspec-casas §03.16): a tela relê as casas. */
  casasPulse: { seq: number; mapa: string } | null;
  swingPulse: { seq: number; ms: number } | null;
  /** `undefined` até o snapshot ou o pack; a barra não trata isso como desarmado. */
  mainHandDefId: string | null | undefined;
  setMainHand(defId: string | null): void;
  setOffHand(defId: string | null): void;
  consumiveis: Readonly<Record<string, number>>;
  /** Caçada em modo Manual. Legível sem abrir o gump — é a etiqueta da barra. */
  huntManual: boolean;
  /** Sobe a cada aviso "HuntFim" do servidor: a ordem de caçada acabou e o mundo relê /hunts. */
  huntFimSeq: number;
  setHuntManual(manual: boolean): void;
  evento: EventoEstado | null;
  /** Chefe do Mundo: a última barra do hub, ou null. */
  chefe: ChefeEstado | null;
  /** Pentagrama (techspec-pentagrama §09): a ponta desperta, o altar da sala, o canal e a ponta ao alcance. */
  penta: PentaEstado | null;
  pentaAltar: readonly PentaAltarLinha[];
  pentaCanal: PentaCanal | null;
  /** O que o servidor disse no último welcome: a staff entrou oculta? Objeto novo a cada welcome. */
  ocultoServidor: { ligado: boolean } | null;
  pentaLugar: LugarPenta;
  pentaForjar(cor: number): void;
  pentaEngastar(cor: number, engastar: boolean): void;
  pentaRitual(): void;
  pentaCancelar(): void;
  avisoAtual: AvisoItem | null;
  fecharAviso(): void;
  /** War de arma. O botão Guerra da barra some quando isto é false. */
  war: boolean;
  /** Sobe quando o servidor credita saque — a mochila refetch. */
  lootPulse: number;
  portal: PortalNoMapa | null;
  confirmarPortal(): void;
  recusarPortal(): void;
  /** `protegida`: a chave "Área protegida" do menu Teleportar (techspec-cacada-protegida §02.1). */
  usarViagem(destino: ViagemDestino, protegida?: boolean): void;
  /** Sobe a cada mudança do livro de runas no servidor ("ru"): o painel relê api/runas. */
  runasSeq: number;
  /** O Mark ou o portal conjurando agora (a barra do painel Runas), ou null. */
  runaRitual: RunaRitualView | null;
  /** O portal de runa que você abriu e ainda está no chão (a contagem na linha da runa). */
  meuPortalRuna: MeuPortalRuna | null;
  /** Grava a runa onde o personagem está (runaId nulo) ou remarca uma; o servidor conjura o Mark. */
  marcarRuna(nome: string | null, cor: number, runaId: string | null): void;
  /** Recall de uma runa (ou "casa"); recusado, a falha da magia. */
  recallRuna(id: string): void;
  /** Abre o portal de uma runa (ou "casa") ao lado do personagem. */
  portalRuna(id: string): void;
  resEstado: ResEstadoView;
  pedirRess(): void;
  /** Criminoso ou assassino na cidade com guarda: o aviso com o estado e a oferta de Cove. */
  resBloqueio: ResBloqueioView | null;
  pedirRessCove(): void;
  fecharResBloqueio(): void;
  /** A oferta de Ressurreição de outro jogador (techspec-ressurreicao), com a hora em que vence. */
  resOferta: { de: string; ate: number } | null;
  responderRess(aceita: boolean): void;
  /** O corpo da última morte ainda no chão (techspec-resgate-do-corpo): o mapa, quando some e quantos itens. */
  corpoPendente: CorpoPendenteView | null;
  fecharCorpoPendente(): void;
  /** O resgate do corpo: 0 nada, 1 indo (escondido), 2 recuperou, 3 não deu. */
  resgate: ResgateView;
  resgatarCorpo(): void;
  cancelarResgate(): void;
  fecharResgate(): void;
}

export interface CorpoPendenteItemView { defId: string; qty: number; vestido: boolean }
export interface CorpoPendenteView { mapa: string; ate: number; itens: number; lista: readonly CorpoPendenteItemView[] }
export interface ResgateView { etapa: number; motivo: string; cacadas: readonly string[] }
/** O Mark (tipo mark) ou o portal (tipo portal) conjurando: `alvo` é a runa (ou "casa"); `inicio` em performance.now(). */
export interface RunaRitualView { tipo: 'mark' | 'portal' | 'recall'; alvo: string; ms: number; inicio: number }
/** O portal que este personagem abriu e está no chão: a runa (ou "casa") e quando fecha (performance.now()). */
export interface MeuPortalRuna { id: string; alvo: string; ate: number }
const RESGATE_PARADO: ResgateView = { etapa: 0, motivo: '', cacadas: [] };

const RES_PARADO: ResEstadoView = { etapa: 0, pontoId: '', cidade: '', x: 0, y: 0 };

/** Preço do VIP por poção até o /vip responder (o servidor manda os de verdade). */
const PRECOS_VIP_PADRAO: Readonly<Record<string, number>> = {
  'greater-healing': 75, 'cure-poison': 35, 'greater-mana': 75, 'greater-cure': 70, refresh: 30, 'total-refresh': 60,
  'strength-potion': 50, 'greater-strength': 90, 'agility-potion': 50, 'greater-agility': 90, nightsight: 30, 'magic-resist': 60
};

const CHAT_MAX = 80;
const TELL_MAX = 40;

function pushChat(list: ChatLine[], line: ChatLine, max: number): ChatLine[] {
  const next = [...list, line];
  return next.length > max ? next.slice(next.length - max) : next;
}

function peerKey(name: string): string {
  return name.trim().toLowerCase();
}

function emptySide(accepted = false): TradeSide {
  return { items: [], gold: 0, accepted };
}

export function useWorld({
  playerName, staffCargo = 0, homeCity = 'britain', characterId, skin, skinHue, dead: isDead = false, notoriety, onDead, net = false, onPlayerMenu, onMapa, onDummyMenu, onPlayerHover, onNpcClick, onBankOpen, onIncomingTell, onTreinoAlvo
}: UseWorldArgs): UseWorldResult {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const minimapRef = useRef<HTMLCanvasElement>(null);
  const paperdollRef = useRef<HTMLCanvasElement>(null);
  const profileDollRef = useRef<HTMLCanvasElement>(null);
  const inventoryDollRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<WorldHandle | null>(null);
  const stepRef = useRef<(seq: number, dir: number, gallop: boolean) => void>(() => {});
  const strikeRef = useRef<((s: NetStrike, meu: boolean, emVoce: boolean, nome: string) => void) | null>(null);
  const strikeCapRef = useRef<(() => void) | null>(null);
  const listenStrike = useCallback((fn: ((s: NetStrike, meu: boolean, emVoce: boolean, nome: string) => void) | null) => {
    strikeRef.current = fn;
  }, []);
  const listenStrikeCap = useCallback((fn: (() => void) | null) => {
    strikeCapRef.current = fn;
  }, []);
  const [youId, setYouId] = useState(0);
  const castRef = useRef<(slot: number, targetId: number) => void>(() => {});
  const attackRef = useRef<(targetId: number) => void>(() => {});
  const trainDummyRef = useRef<(dummyId: number, skill: string) => void>(() => {});
  const stopTrainDummyRef = useRef<() => void>(() => {});
  const treinarAutoRef = useRef<(skill: string) => void>(() => {});
  const portalRef = useRef<(id: string) => Promise<boolean>>(() => Promise.resolve(false));
  const viagemRef = useRef<(id: string, protegida?: boolean) => Promise<boolean>>(() => Promise.resolve(false));
  const resRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const resCoveRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const recusouPortal = useRef<string | null>(null);
  const connRef = useRef<ReturnType<typeof connectCityHub>['connection'] | null>(null);
  const ignored = useRef(new Set<string>());
  const menuRef = useRef(onPlayerMenu);
  menuRef.current = onPlayerMenu;
  const mapaRef = useRef(onMapa);
  mapaRef.current = onMapa;
  const dummyMenuRef = useRef(onDummyMenu);
  dummyMenuRef.current = onDummyMenu;
  const hoverRef = useRef(onPlayerHover);
  hoverRef.current = onPlayerHover;
  const npcClickRef = useRef(onNpcClick);
  npcClickRef.current = onNpcClick;
  const bankOpenRef = useRef(onBankOpen);
  bankOpenRef.current = onBankOpen;
  const incomingRef = useRef(onIncomingTell);
  incomingRef.current = onIncomingTell;
  const treinoAlvoRef = useRef(onTreinoAlvo);
  treinoAlvoRef.current = onTreinoAlvo;
  const deadCb = useRef(onDead);
  deadCb.current = onDead;
  const openTells = useRef(new Set<string>());
  /** Quando saiu o último tell: a recusa que chegar logo depois aparece na conversa. */
  const tellEnviadoEm = useRef(0);
  const you = useRef(playerName);
  you.current = playerName;

  const [status, setStatus] = useState<WorldStatus | null>(null);
  const [journal, setJournal] = useState<readonly JournalLine[]>([]);
  const [coleta, setColeta] = useState<ColetaAoVivo | null>(null);
  const [coletaTravadoEm, setColetaTravadoEm] = useState(0);
  const [ganhoDoPerfil, setGanhoDoPerfil] = useState<GanhosDoPerfil | null>(null);
  const [vip, setVip] = useState<{
    ate: number; mostrar: boolean; prata: number; semBanco: boolean;
    precos: Record<string, number>;
  } | null>(null);
  const vipLote = useRef<{ prata: number; defs: Map<string, number>; timer: number }>({
    prata: 0, defs: new Map(), timer: 0
  });
  const [combat, setCombat] = useState<readonly JournalLine[]>([]);
  const [local, setLocal] = useState<readonly ChatLine[]>([]);
  const [global, setGlobal] = useState<readonly ChatLine[]>([]);
  const [trade, setTrade] = useState<readonly ChatLine[]>([]);
  const [guild, setGuild] = useState<readonly ChatLine[]>([]);
  const [alliance, setAlliance] = useState<readonly ChatLine[]>([]);
  const [guildInvite, setGuildInvite] = useState<GuildInvite | null>(null);
  const [allianceInvite, setAllianceInvite] = useState<AllianceInvite | null>(null);
  const [warInvite, setWarInvite] = useState<WarInvite | null>(null);
  const [tells, setTells] = useState<Record<string, ChatLine[]>>({});
  const [tellNotice, setTellNotice] = useState<string | null>(null);
  const [unreadTells, setUnreadTells] = useState<Record<string, number>>({});
  const [sala, setSala] = useState<{ mapaId: string; mapaNome: string; npcs: readonly (NpcNoMapa & { remoteId: number })[]; portais: readonly PortalNoMapa[]; viagens: readonly ViagemDestino[] }>({
    mapaId: '', mapaNome: '', npcs: [], portais: [], viagens: []
  });
  const [deal, setDeal] = useState<TradeState | null>(null);
  const [dealInvite, setDealInvite] = useState<string | null>(null);
  const [dueloConvite, setDueloConvite] = useState<DueloConvite | null>(null);
  const [dueloEnviado, setDueloEnviado] = useState<DueloEnviado | null>(null);
  const [duelo, setDuelo] = useState<DueloEstado | null>(null);
  const [dueloResultado, setDueloResultado] = useState<DueloEstado | null>(null);
  const dueloRef = useRef(duelo);
  const dueloIdRef = useRef('');
  const saiuPeloBotao = useRef(false);
  dueloRef.current = duelo;
  const [dueloVoltar, setDueloVoltar] = useState<DueloVoltar | null>(null);
  const [party, setParty] = useState<PartyView | null>(null);
  const [partyInvite, setPartyInvite] = useState<string | null>(null);
  const partyRef = useRef(party);
  partyRef.current = party;
  // O motor sabe quem é do grupo: o alvo amigo não vira ataque nem some no lock do War (techspec-grupo-placas).
  useEffect(() => { handleRef.current?.setGrupo(party?.members.map(m => m.name) ?? []); }, [party]);
  // Duelo v2: a placa do alvo presa no adversário da contagem ao fim (a vida saiu do topo do duelo).
  const adversarioDuelo = duelo && ['carregando', 'contagem', 'luta', 'ausente'].includes(duelo.estado) ? duelo.adversario : null;
  const adversarioDueloRef = useRef(adversarioDuelo);
  adversarioDueloRef.current = adversarioDuelo;
  useEffect(() => { handleRef.current?.setAdversarioDuelo(adversarioDuelo); }, [adversarioDuelo]);
  const [corpses, setCorpses] = useState<CorpseContents[]>([]);
  const [ready, setReady] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [carga, setCarga] = useState<CargaTela>(() => ({ etapa: 'conectar', cidade: nomeDaCidade(homeCity), viagem: false }));
  const [castRefuse, setCastRefuse] = useState(0);
  const [consumeRefuse, setConsumeRefuse] = useState(0);
  const [consumePulse, setConsumePulse] = useState<{ seq: number; id: string; ms: number } | null>(null);
  const consumePulseSeq = useRef(0);
  const [huntManual, setHuntManual] = useState(false);
  /** Sobe a cada "HuntFim": o `useCacadaAtiva` relê a ordem mesmo sem troca de mapa. */
  const [huntFimSeq, setHuntFimSeq] = useState(0);
  const [evento, setEvento] = useState<EventoEstado | null>(null);
  const [chefe, setChefe] = useState<ChefeEstado | null>(null);
  const [penta, setPenta] = useState<PentaEstado | null>(null);
  const [pentaAltar, setPentaAltar] = useState<readonly PentaAltarLinha[]>([]);
  const [pentaCanal, setPentaCanal] = useState<PentaCanal | null>(null);
  const [ocultoServidor, setOcultoServidor] = useState<{ ligado: boolean } | null>(null);
  const [pentaLugar, setPentaLugar] = useState<LugarPenta>(null);
  useEffect(() => {
    const uniao = pentaAltar.reduce((m, l) => m | l.mascara, 0);
    handleRef.current?.setPenta(penta ? penta.cor : -1, uniao, pentaAltar.some(l => l.ritual));
  }, [penta, pentaAltar]);
  const filaAvisos = useRef(filaVazia());
  const [avisoAtual, setAvisoAtual] = useState<AvisoItem | null>(null);
  const [war, setWar] = useState(false);
  const [dummyTrain, setDummyTrain] = useState<{
    skill: string;
    dummyId: number;
    skills: readonly { id: string; value: number; startValue: number }[];
  } | null>(null);
  const [lootPulse, setLootPulse] = useState(0);
  const [portal, setPortal] = useState<PortalNoMapa | null>(null);
  const [resEstado, setResEstado] = useState<ResEstadoView>(RES_PARADO);
  const [resBloqueio, setResBloqueio] = useState<ResBloqueioView | null>(null);
  const [resOferta, setResOferta] = useState<{ de: string; ate: number } | null>(null);
  const [corpoPendente, setCorpoPendente] = useState<CorpoPendenteView | null>(null);
  const [resgate, setResgate] = useState<ResgateView>(RESGATE_PARADO);
  const [castPulse, setCastPulse] = useState<CastPulse | null>(null);
  const castPulseSeq = useRef(0);
  const [recargaPulse, setRecargaPulse] = useState<RecargaPulse | null>(null);
  const [casasPulse, setCasasPulse] = useState<{ seq: number; mapa: string } | null>(null);
  const [runasSeq, setRunasSeq] = useState(0);
  const [runaRitual, setRunaRitual] = useState<RunaRitualView | null>(null);
  const [meuPortalRuna, setMeuPortalRuna] = useState<MeuPortalRuna | null>(null);
  /** A runa do último portal que deu certo: o "pg" seguinte com o seu nome é dele. */
  const alvoPortalRuna = useRef('');
  /**
   * O som da viagem aceita ("va") depende de como ela saiu: atravessar portal no chão é o som de entrar no portal
   * (0x20E), o recall da runa é o Recall, e o menu Teleportar é o Gate Travel de sempre.
   */
  const somDaViagem = useRef<'portal' | 'recall' | 'menu'>('menu');
  const recargaPulseSeq = useRef(0);
  const [swingPulse, setSwingPulse] = useState<{ seq: number; ms: number } | null>(null);
  const swingPulseSeq = useRef(0);
  const [mainHandDefId, setMainHandDefId] = useState<string | null | undefined>(undefined);
  const [consumiveis, setConsumiveis] = useState<Record<string, number>>({});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let handle: WorldHandle | null = null;
    let dead = false;
    let fantasma = isDead;
    let packRetry = 0;
    const ac = new AbortController();
    setReady(false);
    setFailure(null);
    setCarga({ etapa: 'conectar', cidade: nomeDaCidade(homeCity), viagem: false });
    setMainHandDefId(undefined);

    const aplicarPack = (pack: Pack, morto: boolean) => {
      if (morto) {
        handle?.setMount(null);
        handle?.setCloth(null);
        handle?.setMainHand(null);
        setMainHandDefId(null);
        handle?.setOffHand(null);
        return;
      }
      handle?.setMount(pack.items.find(i => i.equippedSlot === 'mount')?.defId ?? null);
      handle?.setCloth((pack.cloth ?? overlayDoPack(pack.items)) || null);
      const main = pack.items.find(i => i.equippedSlot === 'mainHand')?.defId ?? null;
      handle?.setMainHand(main);
      setMainHandDefId(main);
      handle?.setOffHand(pack.items.find(i => i.equippedSlot === 'offHand')?.defId ?? null);
      setConsumiveis(consumiveisDoPack(pack));
    };
    const buscarPack = (morto: boolean) => {
      window.clearTimeout(packRetry);
      let tries = 0;
      const load = () => {
        api.getPack()
          .then(pack => { if (!dead) aplicarPack(pack, morto); })
          .catch(() => {
            if (dead) return;
            tries += 1;
            if (tries < 8) packRetry = window.setTimeout(load, 350);
          });
      };
      load();
    };

    void carregarMapaPorCidade(homeCity, ac.signal).then(mapa => {
      if (dead) return;
      handle = createWorld({
        canvas,
        mapa,
        minimap: minimapRef.current,
        paperdoll: paperdollRef.current,
        profileDoll: profileDollRef.current,
        inventoryDoll: inventoryDollRef.current,
        playerName,
        staffCargo,
        skin,
        skinHue,
        dead: isDead,
        notoriety,
        net,
        onCarga(c) {
          if (dead) return;
          setCarga(c);
          // Na arena de duelo, a contagem espera os dois clientes carregarem; fora dela o servidor ignora.
          if (c.etapa === 'pronto' && connRef.current) void dueloCarregouOnHub(connRef.current).catch(() => undefined);
        },
        onDead(next) {
          fantasma = next;
          if (next) {
            handle?.setMount(null);
            handle?.setCloth(null);
            handle?.setMainHand(null);
            setMainHandDefId(null);
            handle?.setOffHand(null);
            setPortal(null);
            setCorpses([]);
          } else {
            setResEstado(RES_PARADO);
            setResBloqueio(null);
            setResOferta(null);
            buscarPack(false);
            // Voltou à vida: o corpo ainda está no chão? O servidor responde no evento `cp`.
            if (net) window.setTimeout(() => {
              const conn = connRef.current;
              if (conn) void corpoPendenteOnHub(conn).catch(() => undefined);
            }, 600);
          }
          if (next) { setCorpoPendente(null); setResgate(RESGATE_PARADO); }
          deadCb.current?.(next);
        },
        onStep: (seq, dir, gallop) => stepRef.current(seq, dir, gallop),
        onFalaLocal(line) {
          if (dead) return;
          setLocal(cur => pushChat([...cur], { via: 0, ...line }, CHAT_MAX));
        },
        ignorado: name => ignored.current.has(name.trim().toLowerCase()),
        onStatus: setStatus,
        onJournal: setJournal,
        onCombat: setCombat,
        onSwingStarted(ms) {
          if (dead) return;
          swingPulseSeq.current += 1;
          setSwingPulse({ seq: swingPulseSeq.current, ms });
        },
        onWar(active) {
          if (dead) return;
          setWar(active);
        },
        onKeepAttack(id) {
          if (dead) return;
          attackRef.current(id);
        },
        onStrike(s, meu, emVoce, nome) { strikeRef.current?.(s, meu, emVoce, nome); },
        onStrikeCap() { strikeCapRef.current?.(); },
        onYouId: setYouId,
        onMainHand(defId) {
          if (dead) return;
          setMainHandDefId(defId);
        },
        onPentaLugar(l) {
          if (!dead) setPentaLugar(l);
        },
        onPortal(next) {
          if (dead) return;
          if (!next) {
            recusouPortal.current = null;
            handleRef.current?.travarPasso(false);
            // Gump fica até Viajar/Ficar: o aceite ainda vale nos SQMs próximos.
            return;
          }
          if (recusouPortal.current === next.id) return;
          handleRef.current?.travarPasso(true);
          setPortal(next);
        },
        onColetaTravada() {
          if (dead) return;
          setColetaTravadoEm(Date.now());
          sayKey('coleta.travado', undefined, 'warn');
        },
        onPortalClick(next) {
          if (dead) return;
          recusouPortal.current = null;
          handleRef.current?.travarPasso(true);
          setPortal(next);
        },
        onPlayerMenu(name, x, y, tag, cargo, info) { menuRef.current?.(name, x, y, tag, cargo, info); },
        onMapa(id) { mapaRef.current?.(id); },
        onSala() {
          const h = handleRef.current;
          if (!h) return;
          setSala({ mapaId: h.mapaId(), mapaNome: h.mapaNome(), npcs: h.npcsDaSala(), portais: h.portaisDaSala(), viagens: h.viagensDoMenu() });
        },
        onDummyMenu(id, x, y) { dummyMenuRef.current?.(id, x, y); },
        onPlayerHover(hit) { hoverRef.current?.(hit); },
        onNpcClick(id, nome, titulo, x, y, temLoja, temCorretor, temBanco, remoteId, alcance) {
          npcClickRef.current?.(id, nome, titulo, x, y, temLoja, temCorretor, temBanco, remoteId, alcance);
        },
        onCorpseClick(ids) {
          if (fantasma) return;
          const conn = connRef.current;
          if (!conn) return;
          for (const id of ids) void openCorpseOnHub(conn, id);
        },
        onIgnore(name, ignoring) {
          const key = name.trim().toLowerCase();
          if (!key) return;
          if (ignoring) ignored.current.add(key);
          else ignored.current.delete(key);
        }
      });
      handleRef.current = handle;
      handle.setGrupo(partyRef.current?.members.map(m => m.name) ?? []);
      handle.setAdversarioDuelo(adversarioDueloRef.current);
      setSala({ mapaId: handle.mapaId(), mapaNome: handle.mapaNome(), npcs: handle.npcsDaSala(), portais: handle.portaisDaSala(), viagens: handle.viagensDoMenu() });
      setReady(true);
      void api.vip().then(v => {
        if (!v) return;
        setVip({
          ate: v.ate ?? 0,
          mostrar: v.mostrar !== false,
          prata: v.banco ?? 0,
          semBanco: (v.banco ?? 0) <= 0,
          precos: v.precos ?? PRECOS_VIP_PADRAO
        });
      }).catch(() => {});
      void prefetchFolhas(skin);
      if (isDead) {
        handle.setMount(null);
        handle.setCloth(null);
        handle.setMainHand(null);
        handle.setOffHand(null);
        setMainHandDefId(null);
      } else {
        buscarPack(false);
      }

      if (net && characterId) {
        definirBuffs([]);
        limparEfeitosDeOutros();
        const { connection, sendStep, sendCast, sendAttack, sendTrainDummy, sendStopTrainDummy, sendTreinarAuto, sendPortal, sendViagem, sendRes, sendResCove } = connectCityHub(
          handle,
          characterId,
          accept,
          pack => {
            if (dead) return;
            setGlobal(pack.global.filter(l => !ignored.current.has(l.name.trim().toLowerCase())));
            setTrade(pack.trade.filter(l => !ignored.current.has(l.name.trim().toLowerCase())));
            setGuild((pack.guild ?? []).filter(l => !ignored.current.has(l.name.trim().toLowerCase())));
            setAlliance((pack.alliance ?? []).filter(l => !ignored.current.has(l.name.trim().toLowerCase())));
          },
          {
            onTell: acceptTell,
            onTellCatchup(pack: TellCatchup) {
              if (dead || !pack.with) return;
              const key = peerKey(pack.with);
              setTells(cur => ({
                ...cur,
                [key]: pack.lines
                  .filter(l => !ignored.current.has(l.name.trim().toLowerCase())
                    || l.name.trim().toLowerCase() === you.current.trim().toLowerCase())
                  .map(l => ({ ...l, with: pack.with }))
              }));
            },
            onTellNotice: setTellNotice,
            tellPendente: () => Date.now() - tellEnviadoEm.current < 3000,
            onTradeInvite(name) { if (!dead) setDealInvite(name); },
            onTradeState(state) { if (!dead) { setDealInvite(null); setDeal(state); } },
            onDueloConvite(convite) { if (!dead) setDueloConvite(convite); },
            onDueloEnviado(enviado) { if (!dead) setDueloEnviado(enviado); },
            onDueloConviteFim() {
              if (dead) return;
              setDueloEnviado(null);
              setDueloConvite(null);
            },
            onDueloEstado(estado) {
              if (saiuPeloBotao.current && dueloIdRef.current === estado.duelo) return;
              saiuPeloBotao.current = false;
              dueloIdRef.current = estado.duelo;
              dueloRef.current = estado;
              setDuelo(estado);
              setDueloResultado(null);
            },
            onDueloVida(vida) {
              if (saiuPeloBotao.current) return;
              setDuelo(cur => {
                if (!cur) return cur;
                const prox = { ...cur, vidaVoce: vida.voce, vidaEle: vida.ele };
                dueloRef.current = prox;
                return prox;
              });
            },
            onDueloVoltar(volta) { if (!dead) setDueloVoltar(volta); },
            onTradeOver(over) {
              if (dead) return;
              // `TradeOverCode` é o mesmo número do fio; a frase sai do resource.
              const chaveOver = CHAVE_TRADE_OVER[over.code];
              if (chaveOver) handle?.sayKey(chaveOver, { nome: over.message }, over.code === 1 ? 'system' : 'note');
              else if (over.message) handle?.say(over.message, over.code === 1 ? 'system' : 'note');
              if (over.code === 1) setLootPulse(n => n + 1);
              if (over.code === 6) {
                setDeal(cur => cur
                  ? {
                      ...cur,
                      mine: { ...cur.mine, accepted: false },
                      theirs: { ...cur.theirs, accepted: false }
                    }
                  : cur);
                return;
              }
              setDeal(null);
              setDealInvite(null);
            },
            onPartyInvite(name) { if (!dead) setPartyInvite(name); },
            onPartyState(state) {
              if (dead) return;
              setPartyInvite(null);
              // Guid vazio é o "sem grupo" do servidor: um estado, e não a
              // ausência de estado — assim o gump some sem precisar de outra
              // letra só para dizer que o grupo acabou.
              setParty(isEmptyGuid(state.id) ? null : state);
            },
            onColeta(estado) {
              if (dead) return;
              setColeta(estado.ativa ? estado : null);
              handleRef.current?.travarColeta(estado.ativa);
            },
            onColetaTravado() {
              if (!dead) setColetaTravadoEm(Date.now());
            },
            onGanhoDoPerfil(ganho) {
              // Em fila: o setState em rajada (uma morte sobe várias skills) não pode perder o do meio.
              setGanhoDoPerfil(cur => {
                const seq = (cur?.seq ?? 0) + 1;
                return { seq, itens: [...(cur?.itens ?? []).slice(-63), { ...ganho, seq }] };
              });
            },
            onVip(estado) {
              setVip(cur => ({
                ...estado,
                precos: cur?.precos ?? PRECOS_VIP_PADRAO
              }));
            },
            onBuffs(lista) {
              definirBuffs(lista);
            },
            onEfeitosDeOutro(id, lista) {
              definirEfeitosDe(id, lista);
            },
            onVipDebito(d) {
              setVip(cur => cur
                ? { ...cur, prata: d.prata, semBanco: false }
                : cur);
              flutuarPrata(d.preco);
              const lote = vipLote.current;
              lote.prata += d.preco;
              lote.defs.set(d.def, (lote.defs.get(d.def) ?? 0) + 1);
              if (lote.timer) return;
              lote.timer = window.setTimeout(() => {
                const nome = (def: string) => nomeDoItem(def);
                const partes = [...lote.defs.entries()].map(([def, q]) => `${q} × ${nome(def)}`);
                anotarDiario(t('vip.diario', { texto: `${partes.join(', ')}, −${lote.prata} prata` }));
                lote.prata = 0;
                lote.defs.clear();
                lote.timer = 0;
              }, 60_000);
            },
            onPartyVital(v) {
              if (dead) return;
              // O delta chega chaveado por characterId, e não por ShortId: o
              // ShortId é da sala, e o companheiro noutro mapa não existe nela.
              setParty(cur => {
                if (!cur) return cur;
                // O "noutro mapa" é derivado AQUI, e não no servidor: o delta
                // sai por grupo, e o servidor não sabe a cidade de quem lê.
                // Sem isto, o primeiro ponto de dano no companheiro distante
                // apagava o rótulo e ele passava a parecer estar do seu lado.
                // E é recalculado para TODOS a cada delta: quando é você que muda de mapa, o delta que chega é o
                // seu, e o companheiro que ficou (ou que já estava onde você chegou) não manda nada se a vida dele
                // não mudou. Sem isto, quem voltou para o seu lado seguia "em {cidade}", sem barras (Renato, 30/10).
                const minha = sameGuid(v.characterId, characterId)
                  ? v.city
                  : cur.members.find(m => sameGuid(m.characterId, characterId))?.city ?? '';
                const longeDeMim = (city: string) => !!city && !!minha && city !== minha;
                return {
                  ...cur,
                  members: cur.members.map(m => {
                    if (!sameGuid(m.characterId, v.characterId)) {
                      // Morto e offline ficam como estão; vivo e "noutro mapa" trocam conforme a sua cidade nova.
                      if (m.state !== 0 && m.state !== 3) return m;
                      const estado = longeDeMim(m.city) ? 3 : 0;
                      return estado === m.state ? m : { ...m, state: estado };
                    }
                    const longe = v.state === 0 && longeDeMim(v.city);
                    return {
                      ...m,
                      state: longe ? 3 : v.state,
                      hits: v.hits,
                      hitsMax: v.hitsMax,
                      city: v.city,
                      unknown: false,
                      mana: v.mana,
                      manaMax: v.manaMax,
                      stam: v.stam,
                      stamMax: v.stamMax,
                      retrato: v.retrato ?? m.retrato
                    };
                  })
                };
              });
            },
            onCastRefuse() { if (!dead) setCastRefuse(n => n + 1); },
            onCastEstado(spellId, ms, trava) {
              if (dead) return;
              castPulseSeq.current += 1;
              setCastPulse({ seq: castPulseSeq.current, id: spellId, ms, trava });
            },
            onViagemAceita() {
              const som = somDaViagem.current;
              somDaViagem.current = 'menu';
              if (dead) return;
              // O Recall da runa já tocou o som da magia no impacto ("x"); a viagem não repete.
              if (som === 'portal') playPortal();
              else if (som === 'menu') handleRef.current?.playSpell('gate-travel');
            },
            onCasasMudaram(mapa) { if (!dead) setCasasPulse(cur => ({ seq: (cur?.seq ?? 0) + 1, mapa })); },
            // Livro de runas (techspec-livro-de-runas): o livro, a conjuração e os portais no chão.
            onRunasMudaram() { setRunasSeq(n => n + 1); },
            onRunaRitual(r) {
              setRunaRitual(r.ativo ? { tipo: r.tipo, alvo: r.alvo, ms: r.ms, inicio: performance.now() } : null);
              if (!r.ativo && r.ok && r.tipo === 'portal') alvoPortalRuna.current = r.alvo;
              if (!r.ativo && r.ok && r.tipo === 'recall') somDaViagem.current = 'recall';
              // O som da magia Mark quando a runa sai (o do portal toca no "pg", quando ele aparece).
              if (!r.ativo && r.ok && r.tipo === 'mark' && !dead) handleRef.current?.playSpell('mark');
            },
            onPortalRuna(p) {
              handleRef.current?.abrirPortalRuna({
                id: p.id, x: p.x, y: p.y, destinoMapa: p.destinoMapa, destX: 0, destY: 0,
                efeito: p.vermelho ? 'gate-travel-red' : 'gate-travel-opened',
                rotulo: p.dono ? `${p.rotulo} (${p.dono})` : p.rotulo
              }, p.ms);
              if (!dead) handleRef.current?.playSpell('gate-travel');
              if (p.dono === playerName && alvoPortalRuna.current) {
                setMeuPortalRuna({ id: p.id, alvo: alvoPortalRuna.current, ate: performance.now() + p.ms });
                alvoPortalRuna.current = '';
              }
            },
            onRunaRecallFalhou() { somDaViagem.current = 'menu'; },
            onPortalRunaFim(id) {
              handleRef.current?.fecharPortalRuna(id);
              setMeuPortalRuna(cur => (cur?.id === id ? null : cur));
            },
            onRecargaMagia(spellId, ms, globalMs) {
              recargaPulseSeq.current += 1;
              setRecargaPulse({ seq: recargaPulseSeq.current, id: spellId, ms, globalMs });
            },
            onConsumeRefuse() { if (!dead) setConsumeRefuse(n => n + 1); },
            // Uma sessão por conta: a tela de carregamento mostra o motivo, com "Tentar de novo" (que toma a vaga de
            // volta e desconecta o outro aparelho) e "Voltar".
            onSessaoSubstituida() { if (!dead) setFailure(t('mundo.sessaoSubstituida')); },
            onConsumeUsed(id, ms) {
              if (dead || !id) return;
              consumePulseSeq.current += 1;
              setConsumePulse({ seq: consumePulseSeq.current, id, ms });
            },
            onHuntMode(manual) { if (!dead) setHuntManual(manual); },
            onHuntFim() {
              if (dead) return;
              setHuntManual(false);
              setHuntFimSeq(n => n + 1);
            },
            onAcessoEncerrado() {
              if (dead) return;
              // Sem reconexão automática: a sessão já não vale. A tela de entrada mostra o motivo.
              void connRef.current?.stop();
              avisarAcessoEncerrado();
            },
            onGuildInvite(invite) { if (!dead) setGuildInvite(invite); },
            onAllianceInvite(invite) { if (!dead) setAllianceInvite(invite); },
            onWarInvite(invite) { if (!dead) setWarInvite(invite); },
            onCorpseContents(pack) {
              if (fantasma || !pack.id) return;
              setCorpses(cur => {
                const i = cur.findIndex(c => c.id === pack.id);
                if (i < 0) return [...cur, pack];
                const next = cur.slice();
                next[i] = pack;
                return next;
              });
            },
            onCorpseGone(id) {
              setLootPulse(n => n + 1);
              setCorpses(cur => cur.filter(c => c.id !== id));
            },
            onCarryDelta(defId, qty) {
              if (dead) return;
              setConsumiveis(cur => ({ ...cur, [defId]: Math.max(0, qty) }));
            },
            onPackDirty() {
              setLootPulse(n => n + 1);
            },
            onBankOpen(npc) {
              if (fantasma) return;
              playBanco();
              bankOpenRef.current?.(npc.npcId, npc.nome, npc.x, npc.y, npc.shortId, npc.alcance);
            },
            onResEstado(estado) {
              setResEstado(estado);
              // A viagem começou (Cove ou natal): o aviso do criminoso já fez o papel dele.
              if (estado.etapa !== 0) setResBloqueio(null);
            },
            onCorpoPendente(corpo) {
              setCorpoPendente(corpo.segundos > 0 && corpo.mapa
                ? { mapa: corpo.mapa, ate: Date.now() + corpo.segundos * 1000, itens: corpo.itens, lista: corpo.lista }
                : null);
            },
            onResgate(estado) {
              // A etapa 1 chega duas vezes (na saída e na chegada à sala do corpo): o som do Recall só na primeira.
              setResgate(antes => {
                if (estado.etapa === 1 && antes.etapa !== 1) handleRef.current?.playSpell('gate-travel');
                return { etapa: estado.etapa, motivo: estado.motivo, cacadas: estado.cacadas };
              });
              if (estado.etapa === 1) setCorpoPendente(null);
              // O Hide do resgate é o oculto do servidor: escondido indo (o cinza do oculto); recuperou ou parou, o
              // servidor já revelou, e o cliente segue (sem isso o cinza ficava depois de recuperar o corpo).
              if (estado.etapa === 1) setOcultoServidor({ ligado: true });
              else if (estado.etapa === 2 || estado.etapa === 3) setOcultoServidor({ ligado: false });
              // Parou e o corpo ainda está lá: a pílula do corpo volta (o servidor diz se ainda vale).
              if (estado.etapa === 3 && estado.motivo !== 'sumiu' && estado.motivo !== 'tempo') {
                const conn = connRef.current;
                if (conn) void corpoPendenteOnHub(conn).catch(() => undefined);
              }
            },
            onResOferta(oferta) {
              setResOferta(oferta.segundos > 0 && oferta.de ? { de: oferta.de, ate: Date.now() + oferta.segundos * 1000 } : null);
            },
            onResBloqueio(aviso) {
              setResBloqueio(aviso.notoriedade === 0 ? null : { ...aviso, recebidoEm: Date.now() });
            },
            onEventoEstado(prox) {
              setEvento(prox);
              handleRef.current?.setPortaEvento(!!prox?.portaAberta);
            },
            onChefeEstado(prox) {
              setChefe(prox.id ? prox : null);
            },
            onPentaEstado(prox) {
              setPenta(prox.id && prox.cor >= 0 && prox.fase < 2 ? prox : null);
            },
            onPentaCanal(canal) { setPentaCanal(canal); },
            onOculto(ligado) { setOcultoServidor({ ligado }); },
            onPentaAltar(linhas) { setPentaAltar(linhas); },
            onAvisoFaixa(aviso) {
              const pecas = pecasDoAviso(aviso.code, aviso.args, aviso.realce, aviso.texto);
              const { estado, som } = receberAviso(
                filaAvisos.current,
                {
                  code: aviso.code,
                  args: aviso.args,
                  realce: aviso.realce,
                  texto: pecas.texto,
                  rotulo: pecas.rotulo,
                  icone: pecas.icone,
                  evento: ehAvisoDeEvento(aviso.code)
                },
                Date.now(),
                mostrarAvisosNaTela()
              );
              filaAvisos.current = estado;
              setAvisoAtual(estado.ativo);
              if (som) playAviso();
            },
            onTreinoAlvo(alvo) {
              if (alvo.recall) handleRef.current?.playSpell('recall');
              treinoAlvoRef.current?.(alvo);
            },
            onDummyTrain(skill, dummyId, skills) {
              setDummyTrain(cur => {
                if (!skill) {
                  if (cur) handleRef.current?.sayKey('manequim.parou', undefined, 'note');
                  return null;
                }
                if (!cur || cur.skill !== skill) {
                  handleRef.current?.sayKey('manequim.iniciou', { skill }, 'note');
                  return {
                    skill,
                    dummyId,
                    skills: skills.map(s => ({ id: s.id, value: s.value, startValue: s.value }))
                  };
                }
                return {
                  skill,
                  dummyId,
                  skills: skills.map(s => ({
                    id: s.id,
                    value: s.value,
                    startValue: cur.skills.find(c => c.id === s.id)?.startValue ?? s.value
                  }))
                };
              });
            }
          }
        );
        connRef.current = connection;
        // A conexão caiu e o SignalR tenta de novo: a tela volta por cima do mapa até o boneco estar desenhado.
        connection.onreconnecting(() => {
          if (!dead) setCarga(cur => ({ etapa: 'reconectar', cidade: cur.cidade, viagem: false }));
        });
        // O SignalR desistiu de reconectar: a tela vira a falha, com Tentar de novo e Voltar aos personagens.
        connection.onclose(() => {
          if (!dead) setFailure(cur => cur ?? t('carregamento.conexaoPerdida'));
        });
        stepRef.current = sendStep;
        castRef.current = sendCast;
        attackRef.current = sendAttack;
        trainDummyRef.current = sendTrainDummy;
        stopTrainDummyRef.current = sendStopTrainDummy;
        treinarAutoRef.current = skill => { void sendTreinarAuto(skill).catch(() => handleRef.current?.sayKey('manequim.semVaga', undefined, 'warn')); };
        portalRef.current = sendPortal;
        viagemRef.current = sendViagem;
        resRef.current = sendRes;
        resCoveRef.current = sendResCove;
        void startCityHub(handle, connection, characterId, () => {
          if (!dead) setFailure(t('mundo.enterWorldDenied'));
        }).then(() => {
          if (dead) return;
          void api.itemCatalog()
            .then(cat => prefetchIcons(cat.groups[0]?.items.map(i => i.id) ?? []))
            .catch(() => {});
        });
      } else {
        void api.itemCatalog()
          .then(cat => prefetchIcons(cat.groups[0]?.items.map(i => i.id) ?? []))
          .catch(() => {});
      }
    }).catch(err => {
      if (dead || ac.signal.aborted) return;
      setFailure(err instanceof Error ? err.message : t('mundo.naoDesenhou'));
    });

    const accept = (line: ChatLine) => {
      if (ignored.current.has(line.name.trim().toLowerCase())) return;
      if (line.via === 2) setTrade(cur => pushChat([...cur], line, CHAT_MAX));
      else if (line.via === 4) setGuild(cur => pushChat([...cur], line, CHAT_MAX));
      else if (line.via === 5) setAlliance(cur => pushChat([...cur], line, CHAT_MAX));
      else setGlobal(cur => pushChat([...cur], line, CHAT_MAX));
    };

    const acceptTell = (line: ChatLine) => {
      const peer = (line.with || (line.name.toLowerCase() === you.current.trim().toLowerCase()
        ? ''
        : line.name)).trim();
      if (!peer) return;
      const key = peerKey(peer);
      if (ignored.current.has(line.name.trim().toLowerCase())
          && line.name.trim().toLowerCase() !== you.current.trim().toLowerCase())
        return;
      setTells(cur => ({ ...cur, [key]: pushChat(cur[key] ?? [], { ...line, with: peer }, TELL_MAX) }));
      const mine = line.name.trim().toLowerCase() === you.current.trim().toLowerCase();
      if (!mine) incomingRef.current?.(peer);
      if (!mine && !openTells.current.has(key))
        setUnreadTells(cur => ({ ...cur, [key]: (cur[key] ?? 0) + 1 }));
    };

    return () => {
      dead = true;
      window.clearTimeout(packRetry);
      ac.abort();
      const conn = connRef.current;
      connRef.current = null;
      stepRef.current = () => {};
      if (conn && conn.state !== HubConnectionState.Disconnected)
        void conn.stop();
      handle?.destroy();
      handleRef.current = null;
      setReady(false);
      setWar(false);
      setDummyTrain(null);
      setLocal([]);
      setGlobal([]);
      setTrade([]);
      setJournal([]);
      setCombat([]);
      setGuild([]);
      setAlliance([]);
      setGuildInvite(null);
      setAllianceInvite(null);
      setWarInvite(null);
      setTells({});
      setUnreadTells({});
      setDeal(null);
      setDealInvite(null);
    };
  }, [playerName, staffCargo, homeCity, net, characterId, skin, skinHue, isDead]);

  const say = useCallback((text: string, tone: JournalTone = 'system') => {
    handleRef.current?.say(text, tone);
  }, []);

  const sayKey = useCallback((chave: string, args?: Record<string, string | number>, tone: JournalTone = 'plain') => {
    handleRef.current?.sayKey(chave, args, tone);
  }, []);

  const falarNpc = useCallback((id: number, text: string) => {
    handleRef.current?.falarNpc(id, text);
  }, []);

  const sayToServer = useCallback((via: 0 | 1 | 2 | 4 | 5, text: string) => {
    const conn = connRef.current;
    if (conn && net) {
      void sayOnHub(conn, via, text);
      return;
    }
    // Sem servidor (modo de teste): a fala local vira balão e linha só aqui.
    if (via !== 0) return;
    const limpo = text.trim().slice(0, 80);
    if (!limpo) return;
    handleRef.current?.falarVoce(limpo);
    setLocal(cur => pushChat([...cur], { via: 0, name: you.current, text: limpo, at: Date.now() }, CHAT_MAX));
  }, [net]);

  const tellToServer = useCallback((name: string, text: string) => {
    const conn = connRef.current;
    if (conn && net) {
      setTellNotice(null);
      tellEnviadoEm.current = Date.now();
      void tellOnHub(conn, name, text).catch(() => setTellNotice(t('amigo.naoEnviou')));
      return;
    }
    const key = peerKey(name);
    const line: ChatLine = {
      via: 3, name: playerName, text, at: Date.now(), with: name
    };
    setTells(cur => ({ ...cur, [key]: pushChat(cur[key] ?? [], line, TELL_MAX) }));
  }, [net, playerName]);

  const openTell = useCallback((name: string) => {
    const key = peerKey(name);
    const already = openTells.current.has(key);
    openTells.current.add(key);
    setUnreadTells(cur => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
    const conn = connRef.current;
    if (!already && conn && net) void askTellCatchup(conn, name);
  }, [net]);

  const markTellRead = useCallback((name: string) => {
    const key = peerKey(name);
    openTells.current.delete(key);
  }, []);

  const inviteTrade = useCallback((name: string) => {
    handleRef.current?.sayKey('troca.voceConvidou', { nome: name }, 'system');
    const conn = connRef.current;
    if (conn && net) {
      void inviteTradeOnHub(conn, name);
      return;
    }
    window.setTimeout(() => {
      setDeal({
        id: 'mock',
        peer: name,
        mine: emptySide(),
        theirs: {
          items: [{ id: '', defId: 'katana', name: 'Katana', qty: 1, dur: null }],
          gold: 10,
          accepted: false
        }
      });
    }, 400);
  }, [net]);

  const respondTrade = useCallback((ok: boolean) => {
    const name = dealInvite;
    setDealInvite(null);
    const conn = connRef.current;
    if (conn && net && name) void respondTradeOnHub(conn, name, ok);
  }, [dealInvite, net]);

  const invitePartyMember = useCallback((name: string) => {
    const conn = connRef.current;
    if (conn && net) void invitePartyOnHub(conn, name);
  }, [net]);

  const respondParty = useCallback((ok: boolean) => {
    setPartyInvite(null);
    const conn = connRef.current;
    if (conn && net) void respondPartyOnHub(conn, ok);
  }, [net]);

  const leaveParty = useCallback(() => {
    const conn = connRef.current;
    if (conn && net) void leavePartyOnHub(conn);
    else setParty(null);
  }, [net]);

  const kickPartyMember = useCallback((name: string) => {
    const conn = connRef.current;
    if (conn && net) void kickPartyOnHub(conn, name);
  }, [net]);

  const offerItem = useCallback((item: PackItem, qty?: number) => {
    const n = Math.max(1, Math.min(item.qty, qty ?? item.qty));
    const conn = connRef.current;
    if (conn && net) {
      void offerOnHub(conn, 1, item.id, n);
      return;
    }
    setDeal(cur => {
      if (!cur || item.equippedSlot) return cur;
      const items = cur.mine.items.filter(i => i.id !== item.id);
      const slot: TradeSlot = { id: item.id, defId: item.defId, name: item.name, qty: n, dur: item.dur };
      if (item.defId === 'gold') {
        return { ...cur, mine: { ...cur.mine, gold: n, accepted: false }, theirs: { ...cur.theirs, accepted: false } };
      }
      if (items.length >= 10 && !cur.mine.items.some(i => i.id === item.id)) return cur;
      return {
        ...cur,
        mine: { items: [...items, slot].slice(0, 10), gold: cur.mine.gold, accepted: false },
        theirs: { ...cur.theirs, accepted: false }
      };
    });
  }, [net]);

  const takeBack = useCallback((itemId: string) => {
    const conn = connRef.current;
    if (conn && net) {
      void offerOnHub(conn, 2, itemId, 0);
      return;
    }
    setDeal(cur => {
      if (!cur) return cur;
      return {
        ...cur,
        mine: { ...cur.mine, items: cur.mine.items.filter(i => i.id !== itemId), accepted: false },
        theirs: { ...cur.theirs, accepted: false }
      };
    });
  }, [net]);

  const offerGold = useCallback((qty: number) => {
    const conn = connRef.current;
    if (conn && net) {
      void offerOnHub(conn, 3, null, qty);
      return;
    }
    setDeal(cur => cur
      ? {
          ...cur,
          mine: { ...cur.mine, gold: Math.max(0, qty), accepted: false },
          theirs: { ...cur.theirs, accepted: false }
        }
      : cur);
  }, [net]);

  const finishMock = useCallback(async (state: TradeState) => {
    try {
      const pack = await api.getPack();
      for (const slot of state.mine.items) {
        if (slot.id) await api.sellItem(slot.id, slot.qty);
      }
      if (state.mine.gold > 0) {
        const gold = pack.items.find(i => i.defId === 'gold' && !i.equippedSlot);
        if (gold) await api.sellItem(gold.id, state.mine.gold);
      }
      for (const slot of state.theirs.items) {
        await api.buyItem(slot.defId, slot.qty);
      }
      if (state.theirs.gold > 0) await api.buyItem('gold', Math.min(100, state.theirs.gold));
    } catch { /* mock: a loja a 0 cobre o presente */ }
    handleRef.current?.sayKey('troca.done', { nome: state.peer }, 'system');
    setLootPulse(n => n + 1);
    setDeal(null);
  }, []);

  const readyTrade = useCallback((ready: boolean) => {
    const conn = connRef.current;
    if (conn && net) {
      void readyTradeOnHub(conn, ready);
      return;
    }
    setDeal(cur => {
      if (!cur) return cur;
      const next = { ...cur, mine: { ...cur.mine, accepted: ready } };
      if (ready) {
        window.setTimeout(() => {
          setDeal(now => {
            if (!now) return now;
            const both = { ...now, theirs: { ...now.theirs, accepted: true }, mine: { ...now.mine, accepted: true } };
            void finishMock(both);
            return both;
          });
        }, 250);
      }
      return next;
    });
  }, [net, finishMock]);

  const cancelTrade = useCallback(() => {
    const conn = connRef.current;
    if (conn && net) void cancelTradeOnHub(conn);
    else {
      setDeal(null);
      setDealInvite(null);
      handleRef.current?.sayKey('troca.cancel', undefined, 'note');
    }
  }, [net]);

  const fecharAviso = useCallback(() => {
    const { estado, som } = avancarFila(filaAvisos.current);
    filaAvisos.current = estado;
    setAvisoAtual(estado.ativo);
    if (som) playAviso();
  }, []);

  return {
    listenStrike,
    listenStrikeCap,
    vip,
    coleta,
    coletaTravadoEm,
    ganhoDoPerfil,
    travarColeta(on: boolean) { handleRef.current?.travarColeta(on); },
    bonecoDe(nome: string, canvas: HTMLCanvasElement) { return handleRef.current?.bonecoDe(nome, canvas) ?? (() => {}); },
    marcarJogador(nome: string) { return handleRef.current?.marcarJogador(nome) ?? false; },
    idDoJogador(nome: string) { return handleRef.current?.idDoJogador(nome) ?? null; },
    retratoDoAlvo(canvas: HTMLCanvasElement, id: number) { return handleRef.current?.retratoDoAlvo(canvas, id) ?? (() => {}); },
    retratoDe(canvas: HTMLCanvasElement, nome: string, look: import('../realtime/cityHub').RetratoDoMembro | null) {
      return handleRef.current?.retratoDe(canvas, nome, look) ?? (() => {});
    },
    provador(canvas: HTMLCanvasElement, cloth: string, dir: number, feminino?: boolean) { return handleRef.current?.provador(canvas, cloth, dir, feminino) ?? (() => {}); },
    youId,
    canvasRef, minimapRef, paperdollRef, profileDollRef, inventoryDollRef,
    status, journal, combat, local, global, trade, guild, alliance, tells, tellNotice, unreadTells, sala,
    guildInvite, allianceInvite, warInvite,
    clearGuildInvite() { setGuildInvite(null); },
    clearAllianceInvite() { setAllianceInvite(null); },
    clearWarInvite() { setWarInvite(null); },
    ready, failure, carga,
    say, sayKey, falarNpc, sayToServer, tellToServer, openTell, markTellRead, net,
    deal, dealInvite, inviteTrade, respondTrade, offerItem, takeBack, offerGold, readyTrade, cancelTrade,
    duelo, dueloResultado, dueloConvite, dueloVoltar, dueloEnviado,
    desafiar(nome: string, modo: 'sem_loot' | 'com_loot', aposta: boolean) {
      const conn = connRef.current;
      if (conn && net) void desafiarOnHub(conn, nome, modo, aposta);
    },
    cancelarDesafio() {
      setDueloEnviado(null);
      const conn = connRef.current;
      if (conn && net) void cancelarDesafioOnHub(conn).catch(() => undefined);
    },
    responderDuelo(aceitar: boolean) {
      const convite = dueloConvite;
      setDueloConvite(null);
      const conn = connRef.current;
      if (conn && net) void responderDueloOnHub(conn, aceitar);
      else if (!aceitar && convite) handleRef.current?.sayKey('duelo.recusado', { nome: convite.de }, 'note');
    },
    cancelarMagia() {
      const conn = connRef.current;
      if (conn && net) void cancelarMagiaOnHub(conn).catch(() => undefined);
    },
    desistirDuelo() {
      const conn = connRef.current;
      if (conn && net) void desistirDueloOnHub(conn);
    },
    sairDuelo() {
      saiuPeloBotao.current = true;
      dueloRef.current = null;
      setDuelo(null);
      const conn = connRef.current;
      if (conn && net) void sairDueloOnHub(conn);
    },
    esquecerDuelo() {
      const cur = dueloRef.current;
      if (!cur || cur.estado === 'aposta' || cur.estado === 'indo') return;
      if (!saiuPeloBotao.current && (cur.estado === 'saque' || cur.estado === 'fim' || cur.estado === 'ausente'))
        setDueloResultado(cur);
      dueloRef.current = null;
      setDuelo(null);
    },
    limparDueloResultado() { setDueloResultado(null); },
    voltarDuelo(voltar: boolean) {
      setDueloVoltar(null);
      const conn = connRef.current;
      if (conn && net) void voltarDueloOnHub(conn, voltar);
    },
    party, partyInvite, invitePartyMember, respondParty, leaveParty, kickPartyMember,
    corpses,
    closeCorpse(id?: number) {
      setCorpses(cur => id == null ? [] : cur.filter(c => c.id !== id));
    },
    async takeCorpse(id: number, itemId: string, qty?: number) {
      const conn = connRef.current;
      if (conn && net) {
        await takeCorpseOnHub(conn, id, itemId, qty ?? 0);
        playPegar(itemId, qty ?? 1);
      }
    },
    async takeCorpseAll(id: number, slotIds: readonly string[]) {
      const conn = connRef.current;
      if (conn && net) await takeCorpseAllOnHub(conn, id, slotIds);
    },
    setMount(defId: string | null) { handleRef.current?.setMount(defId); },
    setCloth(defId: string | null) { handleRef.current?.setCloth(defId); },
    setVitals(id: number, hits: number, hitsMax: number, mana: number, manaMax: number, poisoned?: boolean) { handleRef.current?.setVitals(id, hits, hitsMax, mana, manaMax, poisoned); },
    getTargetId() { return handleRef.current?.getTargetId() ?? null; },
    getTargetKind() { return handleRef.current?.getTargetKind() ?? null; },
    getTargetInfo() { return handleRef.current?.getTargetInfo() ?? null; },
    getSelfNotoriety() { return handleRef.current?.getSelfNotoriety() ?? 0; },
    spawnLocalMob(mob) { handleRef.current?.spawnLocalMob(mob); },
    removeLocalMob(id) { handleRef.current?.removeLocalMob(id); },
    pickTile(hint) {
      return new Promise<{ x: number; y: number } | null>(resolve => {
        const handle = handleRef.current;
        if (!handle) { resolve(null); return; }
        handle.beginTilePick((x, y) => resolve({ x, y }), () => resolve(null), hint);
      });
    },
    cancelTilePick() { handleRef.current?.cancelTilePick(); },
    teleportSelf(x: number, y: number) { handleRef.current?.teleportSelf(x, y); },
    setOculto(ligado: boolean) { handleRef.current?.setOculto(ligado); },
    trocarMapa(id: string) { return handleRef.current?.trocarMapa(id) ?? Promise.resolve(); },
    applyFizzle() { handleRef.current?.applyFizzle(); },
    cast(slot: number, targetId: number) { castRef.current(slot, targetId); },
    attack(targetId: number) { attackRef.current(targetId); },
    trainDummy(dummyId: number, skill: string) { trainDummyRef.current(dummyId, skill); },
    stopTrainDummy() { stopTrainDummyRef.current(); },
    approachDummy(dummyId, alcance, onArrive, alvo) {
      return handleRef.current?.approachDummy(dummyId, alcance, onArrive, alvo) ?? false;
    },
    irPara(x, y) {
      return handleRef.current?.irPara(x, y) ?? false;
    },
    treinarAuto(skill: string) { treinarAutoRef.current(skill); },
    manequimDeTreinoPerto() { return handleRef.current?.manequimDeTreinoPerto() ?? null; },
    dummyEm(x, y) { return handleRef.current?.dummyEm(x, y) ?? null; },
    playerScreen() { return handleRef.current?.playerScreen() ?? null; },
    alvoAFrente() { return handleRef.current?.alvoAFrente() ?? null; },
    dummyTrain,
    castRefuse,
    consumeRefuse,
    consumePulse,
    castPulse,
    recargaPulse,
    casasPulse,
    swingPulse,
    mainHandDefId,
    setMainHand(defId: string | null) {
      setMainHandDefId(defId);
      handleRef.current?.setMainHand(defId);
    },
    setOffHand(defId: string | null) {
      handleRef.current?.setOffHand(defId);
    },
    consumiveis,
    huntManual,
    huntFimSeq,
    setHuntManual,
    evento,
    chefe,
    avisoAtual,
    fecharAviso,
    war,
    lootPulse,
    portal,
    confirmarPortal() {
      const atual = portal;
      if (!atual) return;
      setPortal(null);
      if (net) {
        // O som sai no "va" (a sala aceitou): o de entrar no portal. Recusada, a falha da magia (Renato, 02/11/2026).
        somDaViagem.current = 'portal';
        void portalRef.current(atual.id)
          .then(ok => { if (ok === false) { somDaViagem.current = 'menu'; handleRef.current?.falharMagia(); } })
          .catch(() => { somDaViagem.current = 'menu'; handleRef.current?.falharMagia(); })
          .finally(() => {
            handleRef.current?.travarPasso(false);
          });
        return;
      }
      playPortal();
      void (async () => {
        if (handleRef.current && atual.destinoMapa) {
          await handleRef.current.trocarMapa(atual.destinoMapa);
          handleRef.current.teleportSelf(atual.destX, atual.destY);
        }
        handleRef.current?.travarPasso(false);
      })();
    },
    recusarPortal() {
      if (portal) recusouPortal.current = portal.id;
      handleRef.current?.travarPasso(false);
      setPortal(null);
    },
    usarViagem(destino, protegida = false) {
      if (net) {
        // Recusada (espere um pouco, caçando, em combate): a falha da magia, sem o som da viagem. O som sai no "va".
        somDaViagem.current = 'menu';
        void viagemRef.current(destino.id, protegida)
          .then(ok => { if (ok === false) handleRef.current?.falharMagia(); })
          .catch(() => handleRef.current?.falharMagia());
        return;
      }
      handleRef.current?.playSpell('gate-travel');
      void (async () => {
        if (!handleRef.current) return;
        await handleRef.current.trocarMapa(destino.mapa);
        const ponto = pontoDaViagem(destino);
        handleRef.current.teleportSelf(ponto.x, ponto.y);
      })();
    },
    runasSeq,
    runaRitual,
    meuPortalRuna,
    marcarRuna(nome, cor, runaId) {
      const conn = connRef.current;
      if (net && conn) {
        void runaMarcarOnHub(conn, nome, cor, runaId).catch(() => handleRef.current?.falharMagia());
        return;
      }
      // Sem servidor (modo de teste): o Mark de mentira, 2 s, e a runa gravada onde o personagem está.
      const h = handleRef.current;
      if (!h || runaRitual) return;
      h.falarVoce('Kal Por Ylem');
      setRunaRitual({ tipo: 'mark', alvo: runaId ?? '', ms: 2000, inicio: performance.now() });
      window.setTimeout(() => {
        setRunaRitual(null);
        const pos = h.alvoAFrente();
        void api.runaMarcarOffline?.({ nome, cor, runaId, mapa: h.mapaId(), x: pos.x, y: pos.y })
          .then(() => { h.playSpell('mark'); setRunasSeq(n => n + 1); h.sayKey('runa.aviso.gravada', { a: nome ?? h.mapaNome() }, 'spell'); })
          .catch(() => h.falharMagia());
      }, 2000);
    },
    recallRuna(id) {
      const conn = connRef.current;
      if (net && conn) {
        // O Recall é conjurado no servidor com o tempo da caçada: a barra vem pelo "rk", a viagem pelo "va".
        void runaRecallOnHub(conn, id).catch(() => handleRef.current?.falharMagia());
        return;
      }
      handleRef.current?.falharMagia();
    },
    portalRuna(id) {
      const conn = connRef.current;
      if (net && conn) {
        void runaPortalOnHub(conn, id).catch(() => handleRef.current?.falharMagia());
        return;
      }
      handleRef.current?.falharMagia();
    },
    resEstado,
    pedirRess() {
      if (!net) return;
      void resRef.current();
    },
    resBloqueio,
    pedirRessCove() {
      if (!net) return;
      void resCoveRef.current();
    },
    fecharResBloqueio() { setResBloqueio(null); },
    resOferta,
    responderRess(aceita: boolean) {
      setResOferta(null);
      const conn = connRef.current;
      if (conn && net) void responderRessOnHub(conn, aceita).catch(() => undefined);
    },
    corpoPendente,
    fecharCorpoPendente() { setCorpoPendente(null); },
    resgate,
    resgatarCorpo() {
      const conn = connRef.current;
      if (conn && net) void resgatarCorpoOnHub(conn).catch(() => undefined);
    },
    cancelarResgate() {
      const conn = connRef.current;
      if (conn && net) void cancelarResgateOnHub(conn).catch(() => undefined);
    },
    fecharResgate() { setResgate(RESGATE_PARADO); },
    penta, pentaAltar, pentaCanal, pentaLugar, ocultoServidor,
    pentaForjar(cor) { void connRef.current?.send('PentaForjar', cor); },
    pentaEngastar(cor, engastar) { void connRef.current?.send('PentaEngastar', cor, engastar); },
    pentaRitual() { void connRef.current?.send('PentaRitual'); },
    pentaCancelar() {
      setPentaCanal(null);
      void connRef.current?.send('PentaCancelar');
    }
  };
}
